import useClevertapForItinerary from '@/src/hooks/analytics/clevertap/itinerary'
import { TaxDetails } from '@/src/types/itinerary'
import { ValidationState } from '@/src/utils/itinerary/travelerInputValidationRules'
import { Box, Checkbox, InputLabel, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import React, { memo } from 'react'
import ItineraryTextField from '../itinerary/itinearyTextField'

export interface TaxDetailsFormProps extends TaxDetails {
    validationState?: ValidationState
    onChange: (key: keyof TaxDetails, value: string | boolean) => void
    onFieldBlur?: (fieldKey: string, inputValue?: any) => void
}

const TaxDetailsForm: React.FC<TaxDetailsFormProps> = ({
    addTaxChecked,
    companyName,
    taxNo,
    validationState,
    onChange,
    onFieldBlur
}) => {
    const t = useTranslations('itineraryPage.contactDetails')
    const { triggerItineraryEvent } = useClevertapForItinerary()
    // Helper function to get field validation state
    const getFieldValidation = (fieldName: string) => {
        return validationState?.[fieldName] || { isError: false, errorMessage: '', touched: false }
    }

    return (
        <>
            <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'grey.900', mb: 1.5 }}>
                {t('taxDetails')}
            </Typography>
            <Typography sx={{ color: 'grey.600', fontSize: 14, mb: 2 }}>
                {/* Not found in reference: 'To claim credit for the TAX charged by airline, please enter your tax details' */}
                {t('taxDetailsNote')}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Checkbox
                    checked={!!addTaxChecked}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        onChange?.('addTaxChecked', e.target.checked)
                        triggerItineraryEvent('Air_UI_Action', {
                            action_name: 'tax_details',
                            action_type: 'BUTTON',
                            checked: e.target.checked
                        })
                    }}
                    sx={{ width: 16, height: 16, p: 0, mr: 1, color: 'primary.main', borderRadius: 1 }}
                />
                <Typography sx={{ fontSize: 14, fontWeight: 500, color: 'grey.700', cursor: 'pointer' }}>
                    {t('addTaxNumber')}
                </Typography>
            </Box>
            {!!addTaxChecked && (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                        gap: 2,
                        mb: 0
                    }}
                >
                    {/* Tax Number */}
                    <Box>
                        <InputLabel shrink htmlFor="taxno" sx={{ fontWeight: 500, fontSize: 14, color: '#374151' }}>
                            {t('taxDetails')}
                        </InputLabel>
                        <ItineraryTextField
                            id="taxno"
                            placeholder={t('taxNumberPlaceholder')}
                            value={taxNo}
                            onChange={e => onChange?.('taxNo', e.target.value)}
                            onBlur={e => onFieldBlur?.('taxNo', e.target.value)}
                            error={getFieldValidation('taxNo').isError && getFieldValidation('taxNo').touched}
                            helperText={getFieldValidation('taxNo').touched && getFieldValidation('taxNo').errorMessage}
                        />
                    </Box>
                    {/* Company Name */}
                    <Box>
                        <InputLabel
                            shrink
                            htmlFor="company-name"
                            sx={{ fontWeight: 500, fontSize: 14, color: '#374151' }}
                        >
                            {t('companyName')}
                        </InputLabel>
                        <ItineraryTextField
                            id="company-name"
                            placeholder={t('companyNamePlaceholder')}
                            value={companyName}
                            onChange={e => onChange?.('companyName', e.target.value)}
                            onBlur={e => onFieldBlur?.('companyName', e.target.value)}
                            error={
                                getFieldValidation('companyName').isError && getFieldValidation('companyName').touched
                            }
                            helperText={
                                getFieldValidation('companyName').touched &&
                                getFieldValidation('companyName').errorMessage
                            }
                        />
                    </Box>
                    {/* Empty box for grid alignment */}
                    <Box sx={{ display: { xs: 'none', md: 'block' } }} />
                </Box>
            )}
        </>
    )
}

const areEqual = (prevProps: TaxDetailsFormProps, nextProps: TaxDetailsFormProps) => {
    if (
        prevProps.addTaxChecked !== nextProps.addTaxChecked ||
        prevProps.companyName !== nextProps.companyName ||
        prevProps.taxNo !== nextProps.taxNo
    ) {
        return false
    }

    const keys = ['companyName', 'taxNo']
    const extract = (state?: ValidationState) =>
        keys
            .map(k => {
                const v = state?.[k]
                return v ? `${k}:${v.isError ? 1 : 0}:${v.touched ? 1 : 0}:${v.errorMessage || ''}` : `${k}:0:0:`
            })
            .join('|')

    if (extract(prevProps.validationState) !== extract(nextProps.validationState)) return false

    return true
}

export default memo(TaxDetailsForm, areEqual)
